<template>
  <div class="home-container">
    <aside v-if="visibleTabs.length > 0" class="sidebar-shell">
      <div class="sidebar-brand">
        <!--span class="sidebar-badge">Blue Refresh</span-->
        <h2>{{ $tText('home.sidebar.brandTitle', '梯口卫士') }}</h2>
        <p>{{ $tText('home.sidebar.brandDesc', '船舶与人员通行管理平台') }}</p>
      </div>

      <div class="sidebar-nav">
        <div
          v-for="tab in visibleTabs"
          :key="tab.tabKey"
          class="sidebar-tab"
          :class="{ active: activeTab === tab.tabKey }"
          @click="onTabChange(tab.tabKey)"
        >
          <van-icon :name="getTabIcon(tab.tabKey)" class="sidebar-tab__icon" />
          <span>{{ getTabTitle(tab) }}</span>
        </div>
      </div>

      <div class="sidebar-footer">
        <span class="sidebar-status"></span>
        <span>{{ $tText('home.sidebar.onlineStatus', '系统在线运行中') }}</span>
      </div>
    </aside>

    <div class="main-shell">
      <div class="shell-hero">
        <!--div class="shell-hero__copy">
          <span class="shell-hero__eyebrow">通行可视化控制中心</span>
          <h1>{{ currentPageTitle }}</h1>
          <p>蓝色主基调统一手机 H5 与桌面浏览器体验，提升信息层级和可读性。</p>
        </div-->

        <van-nav-bar
          :title="currentPageTitle"
          :left-text="isLoggedIn ? $tText('home.nav.logout', '退出') : ''"
          :left-arrow="isLoggedIn"
          @click-left="onLogout"
          class="nav-bar"
        />
      </div>

      <div class="content-container">
        <router-view />
      </div>

      <div v-if="visibleTabs.length > 0" class="scrollable-tabbar">
        <div class="tabbar-scroll-container">
          <div
            v-for="tab in visibleTabs"
            :key="tab.tabKey"
            class="tabbar-item"
            :class="{ active: activeTab === tab.tabKey }"
            @click="onTabChange(tab.tabKey)"
          >
            <van-icon :name="getTabIcon(tab.tabKey)" class="tabbar-icon" />
            <span class="tabbar-text">{{ getTabTitle(tab) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getDefaultAuthorizedPath,
  getPageTitleConfigByPath,
  getTabKeyByPath,
  getVisibleTabs
} from '@/constants/pagePermissions';

export default {
  name: 'HomePage',
  data() {
    return {
      activeTab: ''
    };
  },
  
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem('token');
    },
    permissionCodes() {
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        return Array.isArray(userInfo.permissionCodes) ? userInfo.permissionCodes : [];
      } catch (error) {
        return [];
      }
    },
    visibleTabs() {
      return getVisibleTabs(this.permissionCodes);
    },
    currentPageTitle() {
      const currentTab = this.visibleTabs.find(tab => tab.tabKey === this.activeTab);
      if (currentTab) {
        return this.getTabTitle(currentTab);
      }

      const titleConfig = getPageTitleConfigByPath(this.$route.path);
      if (titleConfig) {
        return this.$tText(titleConfig.titleKey, titleConfig.defaultTitle);
      }

      return this.$tText('home.default.pageTitle', '船舶管理系统');
    }
  },
  
  methods: {
    getTabTitle(tab) {
      return this.$tText(tab.titleKey, tab.defaultTitle);
    },

    getTabIcon(key) {
      const currentTab = this.visibleTabs.find(tab => tab.tabKey === key);
      return currentTab ? currentTab.icon : 'apps-o';
    },

    syncActiveTab(path) {
      this.activeTab = getTabKeyByPath(path);
    },

    ensureValidRoute() {
      const currentPath = this.$route.path;
      const hasExactTab = this.visibleTabs.some(tab => tab.path === currentPath);
      const hasParentTab = !!getTabKeyByPath(currentPath);

      if (currentPath === '/home' || currentPath === '/home/') {
        this.$router.replace(getDefaultAuthorizedPath(this.permissionCodes));
        return;
      }

      if (!hasExactTab && !hasParentTab && currentPath !== '/home/no-permission') {
        this.$router.replace(getDefaultAuthorizedPath(this.permissionCodes));
      }
    },
    
    onTabChange(name) {
      const targetTab = this.visibleTabs.find(tab => tab.tabKey === name);
      if (!targetTab) {
        return;
      }

      this.activeTab = name;
      this.$router.push(targetTab.path).catch(err => {
        // 捕获并忽略重复导航的错误
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    
    onLogout() {
      this.$dialog.confirm({
        title: this.$tText('home.logout.confirmTitle', '确认退出'),
        message: this.$tText('home.logout.confirmMessage', '您确定要退出系统吗？')
      }).then(() => {
        // 先清除本地登录信息
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('username');
        localStorage.removeItem('rememberLogin');
        this.$toast.success(this.$tText('home.logout.success', '已退出系统'));
        // 用 replace 跳转，避免历史记录冲突
        this.$router.replace('/login');
      }).catch(() => {
        // 用户取消
      });
    }
  },
  
  mounted() {
    this.syncActiveTab(this.$route.path);
    this.ensureValidRoute();
  },
  
  watch: {
    '$route'(to) {
      this.syncActiveTab(to.path);
    }
  }
};
</script>

<style scoped>
.home-container {
  height: 100vh;
  display: flex;
  background: transparent;
  gap: 20px;
  padding: 20px;
}

.sidebar-shell {
  display: none;
  width: 252px;
  flex-shrink: 0;
  border-radius: 30px;
  padding: 24px 18px;
  color: #fff;
  background: linear-gradient(180deg, var(--surface-strong) 0%, var(--surface-strong-alt) 100%);
  box-shadow: 0 20px 48px rgba(12, 55, 123, 0.28);
}

.sidebar-brand {
  padding: 12px 12px 22px;
}

.sidebar-badge {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.14);
  margin-bottom: 16px;
}

.sidebar-brand h2 {
  font-size: 28px;
  line-height: 1.1;
  margin-bottom: 10px;
}

.sidebar-brand p {
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.6;
  font-size: 13px;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.sidebar-tab {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 18px;
  color: rgba(255, 255, 255, 0.82);
  transition: all 0.2s ease;
  cursor: pointer;
}

.sidebar-tab.active {
  color: #fff;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
}

.sidebar-tab__icon {
  font-size: 18px;
}

.sidebar-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: auto;
  padding: 18px 12px 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
}

.sidebar-status {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #42d392;
  box-shadow: 0 0 0 6px rgba(66, 211, 146, 0.16);
}

.main-shell {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shell-hero {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.shell-hero__copy {
  padding: 22px 22px 0;
}

.shell-hero__eyebrow {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(22, 119, 255, 0.1);
  color: var(--brand-700);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
}

.shell-hero__copy h1 {
  font-size: 30px;
  line-height: 1.15;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.shell-hero__copy p {
  max-width: 760px;
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 14px;
}

.nav-bar {
  border-radius: 24px;
  overflow: hidden;
  background: linear-gradient(135deg, var(--surface-strong) 0%, var(--surface-strong-alt) 100%);
  color: white;
  box-shadow: 0 18px 40px rgba(15, 79, 185, 0.18);
}

.nav-bar :deep(.van-nav-bar__title) {
  color: white;
  font-weight: 700;
}

.nav-bar :deep(.van-nav-bar__left) {
  color: white !important;
}

.nav-bar :deep(.van-nav-bar__text) {
  color: white !important;
}

.nav-bar :deep(.van-icon) {
  color: white !important;
}

.content-container {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0 0 78px;
}

.scrollable-tabbar {
  position: sticky;
  bottom: 0;
  height: 64px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(215, 229, 251, 0.9);
  border-radius: 22px;
  box-shadow: 0 16px 32px rgba(15, 79, 185, 0.14);
  backdrop-filter: blur(16px);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.scrollable-tabbar::-webkit-scrollbar {
  display: none;
}

.tabbar-scroll-container {
  display: flex;
  min-width: 100%;
  height: 100%;
}

.tabbar-item {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 100%;
  padding: 5px 0;
  color: var(--text-muted);
  transition: all 0.2s ease;
}

.tabbar-item.active {
  color: var(--brand-700);
}

.tabbar-icon {
  font-size: 20px;
  margin-bottom: 4px;
}

.tabbar-text {
  font-size: 12px;
  line-height: 1;
}

@media (min-width: 1024px) {
  .home-container {
    padding: 24px;
  }

  .sidebar-shell {
    display: flex;
    flex-direction: column;
  }

  .shell-hero {
    gap: 18px;
  }

  .shell-hero__copy {
    padding: 12px 8px 0;
  }

  .content-container {
    padding-bottom: 0;
  }

  .scrollable-tabbar {
    display: none;
  }
}

@media (max-width: 767px) {
  .home-container {
    gap: 14px;
    padding: 14px;
  }

  .shell-hero__copy {
    padding: 8px 4px 0;
  }

  .shell-hero__copy h1 {
    font-size: 24px;
  }

  .nav-bar {
    border-radius: 20px;
  }
}
</style>
